import { useMemo } from 'react';
import { ConstraintFragment } from '@occc/graphql';
import { InfoField } from '@occc-app/design-system';
import { ConstraintBlocksVisibility } from './constraints-section.constants';

import {
  OrderAheadOnlyConstraint,
  InStoreOnlyConstraint,
  FulfillmentChannelConstraint,
  EmailRequiredConstraint,
  FulfillmentWindowConstraint,
  PaymentMethodConstraint,
  PaymentInstrumentConstraint,
  PurchaseThresholdConstraint,
  PurchasableEligibilityConstraint,
  PurchaseEligibleProductGroupConstraint,
  VoucherStackingConstraint,
  BreakageRefundConstraint,
  BreakagePointRoundingConstraint,
  AudienceConstraint,
  StateConstraint,
  SubBrandConstraint,
  IncludedStoresConstraint,
  ExcludedStoresConstraint,
  StoreLocationGroupConstraint,
  getActivationRequiredConstraintBlocks,
} from './components';
import { SectionCard } from '../../components';
import { ConstraintSupplementalData } from '../../component-details.types';
import { arrangeNodeGroups, renderNodeGroups } from '../utils';
import { formatYesNoValues } from '../formatters';
import { BLOCKS_IN_ROW } from '../constants';

type ConstraintsSectionProps = {
  title?: string;
  constraints: ConstraintFragment[];
  visibleBlocks?: ConstraintBlocksVisibility;
  constraintSupplementalData?: ConstraintSupplementalData;
  isStackable?: boolean;
};

export const ConstraintsSection = ({
  title = 'Constraints',
  constraints,
  visibleBlocks,
  constraintSupplementalData,
  isStackable,
}: ConstraintsSectionProps) => {
  const blockGroups = useMemo(() => {
    if (!visibleBlocks) return [];

    return arrangeNodeGroups(
      [
        visibleBlocks.ActivationRequiredConstraint &&
          getActivationRequiredConstraintBlocks(constraints),

        visibleBlocks.FulfillmentWindowConstraint && (
          <FulfillmentWindowConstraint constraints={constraints} />
        ),
        visibleBlocks.EmailRequiredConstraint && (
          <EmailRequiredConstraint constraints={constraints} />
        ),
        visibleBlocks.OrderAheadOnlyConstraint && (
          <OrderAheadOnlyConstraint constraints={constraints} />
        ),
        visibleBlocks.InStoreOnlyConstraint && <InStoreOnlyConstraint constraints={constraints} />,
        visibleBlocks.FulfillmentChannelConstraint && (
          <FulfillmentChannelConstraint constraints={constraints} />
        ),
        visibleBlocks.PaymentMethodConstraint && (
          <PaymentMethodConstraint constraints={constraints} />
        ),
        visibleBlocks.PaymentInstrumentConstraint && (
          <PaymentInstrumentConstraint constraints={constraints} />
        ),
        visibleBlocks.PurchaseThresholdConstraint && (
          <PurchaseThresholdConstraint constraints={constraints} />
        ),
        visibleBlocks.PurchasableEligibilityConstraint && (
          <PurchasableEligibilityConstraint
            constraints={constraints}
            constraintSupplementalData={constraintSupplementalData}
          />
        ),
        visibleBlocks.PurchaseEligibleProductGroupConstraint && (
          <PurchaseEligibleProductGroupConstraint
            constraints={constraints}
            constraintSupplementalData={constraintSupplementalData}
          />
        ),
        visibleBlocks.VoucherStackingConstraint && (
          <VoucherStackingConstraint constraints={constraints} />
        ),
        visibleBlocks.BreakageRefundConstraint && (
          <BreakageRefundConstraint constraints={constraints} />
        ),
        visibleBlocks.BreakagePointRoundingConstraint && (
          <BreakagePointRoundingConstraint constraints={constraints} />
        ),
        visibleBlocks.AudienceConstraint && <AudienceConstraint constraints={constraints} />,
        visibleBlocks.StateConstraint && <StateConstraint constraints={constraints} />,
        visibleBlocks.SubBrandConstraint && <SubBrandConstraint constraints={constraints} />,
        visibleBlocks.IncludedStoresConstraint && (
          <IncludedStoresConstraint constraints={constraints} />
        ),
        visibleBlocks.ExcludedStoresConstraint && (
          <ExcludedStoresConstraint constraints={constraints} />
        ),
        visibleBlocks.StoreLocationGroupConstraint && (
          <StoreLocationGroupConstraint constraints={constraints} />
        ),
        isStackable !== undefined && (
          <InfoField label='Offer Stacking' value={formatYesNoValues([isStackable])} />
        ),
      ],
      BLOCKS_IN_ROW,
    );
  }, [visibleBlocks, constraints, constraintSupplementalData, isStackable]);

  return blockGroups.length ? (
    <SectionCard title={title}>{renderNodeGroups(blockGroups, BLOCKS_IN_ROW)}</SectionCard>
  ) : null;
};
